import * as React from "react";
import { Alert, Snackbar } from "@mui/material";
import { Document } from "../../../types/Document";
import { ExtendedAccount } from "../../../types/ExtendedAccount";
import { Account, AccountNextStep, Person, ServiceProviderType } from "@/types/index";
import { cleanedErrors } from "@/utils/Functions";
import { TextInput } from "../../../components/atoms/TextInput";
import { AccountNextStepInput } from "../../../components/molecules/AccountNextStepInput";
import { FormField } from "../../../components/molecules/FormField";
import { ServiceProvider } from "@/types/index";
import { useOnChange, useUpdater } from "@/utils/callbacks";
import { Record as ServiceProviderRecord } from "./ServiceProviderFields";
import { NestedErrors } from "@/utils/Types";
import { Responsible, errorsOfResponsible, responsibleFromAccount, ResponsibleForm } from "./ResponsibleFields/ResponsibleFields";

export type DefaultAccount = {
  readonly holderNumber?: string;
  readonly accountRequest?: AccountNextStep;
  readonly responsible?: Responsible;
};

export type DefaultAccountErrors = NestedErrors<DefaultAccount> | undefined;

export function errorsOf(record: DefaultAccount): DefaultAccountErrors {
  return cleanedErrors({
    accountRequest: record?.accountRequest ? undefined : "required",
    holderNumber: record?.holderNumber ? undefined : "required",
    responsible: errorsOfResponsible(record?.responsible, record?.accountRequest),
  });
};

export const recordFromAccount = (account?: Account): DefaultAccount => {
  if (!account) {
    return {
      responsible: responsibleFromAccount(account),
    };
  }

  return {
    holderNumber: account.holderNumber,
    accountRequest: account.nextSteps,
    responsible: responsibleFromAccount(account),
  };
};

export type DefaultAccountFieldsProps = {
  readonly persons: ReadonlyArray<Person>;
  readonly serviceProvider: ServiceProviderRecord;
  readonly account?: Account;
  readonly setBusy: (b: boolean) => void;
  readonly uploadedFileInfo: (id: string) => Promise<Document>;
  readonly onAccountAdded: (
    a: ExtendedAccount,
    p?: { newServiceProvider?: ServiceProvider; newPerson?: Person }
  ) => void;
  readonly updateTemplate?: (p: any) => void;

  readonly saveAccount: (r: { record: any }) => Promise<any>;
  readonly saveRecord?: (r: any) => void;
  readonly savedRecord?: any;
  readonly askForDeceasedDetails?: boolean;
};

export const DefaultAccountFields: React.FC<DefaultAccountFieldsProps> = ({
  persons,
  serviceProvider,
  account,
  setBusy,
  onAccountAdded,
  updateTemplate,
  saveAccount,
  saveRecord,
  savedRecord,
}) => {
  const [record, update] = React.useState(savedRecord || recordFromAccount(account));
  const [errors, setErrors] = React.useState({} as DefaultAccountErrors);
  const [remoteErrors, setRemoteErrors] = React.useState(undefined as string | undefined);


  const onAccountRequestValueChange = React.useCallback(
    (accountRequest) => {
      update((s: any) => ({
        ...s,
        accountRequest,
        responsible: {},
      }));
    },
    [update]
  );

  const updateAccountHolder = useUpdater(update, "responsible");
  const onChange = useOnChange(update);

  React.useEffect(() => {
    if (!updateTemplate) {
      return;
    }

    updateTemplate({
      onNext: !serviceProvider?.serviceProviderType
        ? undefined
        : () => {
          const errors = errorsOf(record);

          if (errors) {
            setErrors(errors);
            if (saveRecord) {
              saveRecord({ source: record });
            }
            return;
          }

          const responsible = {
            id: record.responsible?.responsibleId || undefined,
            details: record.responsible?.responsible || undefined,
            bankAccount: record.responsible?.responsibleBankAccount || undefined,
          };

          const data = {
            id: account?.id,
            serviceProvider: {
              id: serviceProvider.serviceProviderId,
              companyName: serviceProvider.customProviderName,
              serviceProviderType: serviceProvider.serviceProviderType,
            },

            holderNumber: record.holderNumber,
            nextStep: record.accountRequest,
            responsible,
          };

          if (saveRecord) {
            saveRecord({ source: record, target: data });
            return { response: {} };
          }

          setBusy(true);

          return saveAccount({
            record: data,
          }).then(
            (
              response: {
                data: {
                  account: ExtendedAccount;
                  newServiceProvider?: ServiceProvider;
                  newPerson?: Person;
                };
              } & { error: Error }
            ) => {
              setBusy(false);
              if (response.error) {
                setRemoteErrors(response.error.message);
                return;
              }
              if (response.data) {
                onAccountAdded(response.data.account, {
                  newServiceProvider: response.data.newServiceProvider,
                  newPerson: response.data.newPerson,
                });
                return;
              }
            },
            (err: Error) => {
              console.warn({ err });
              setBusy(false);
              setRemoteErrors("Operation failed. Please try again or contact customer support.");
            }
          );
        },
    });
  }, [updateTemplate, serviceProvider, account, onAccountAdded, record, setBusy]);

  return (
    <>


      <FormField
        halfWidthByItself
        label={
          serviceProvider.serviceProviderType === ServiceProviderType.Mobile
            ? "Mobile number"
            : "Customer reference number (if not known write N/A)"
        }
      >
        <TextInput
          name="holderNumber"
          value={record.holderNumber || " "}
          onChange={onChange}
          error={errors?.holderNumber}
        />
      </FormField>

      <FormField label="Next steps">
        <AccountNextStepInput
          onValueChange={onAccountRequestValueChange}
          value={record.accountRequest || ""}
          serviceProviderType={serviceProvider.serviceProviderType || ServiceProviderType.Other}
          error={errors?.accountRequest}
        />
      </FormField>

      {record.responsible && record.accountRequest && (
        <ResponsibleForm
          serviceProvider={serviceProvider}
          persons={persons}
          nextStep={record.accountRequest}
          record={record.responsible}
          errors={errors?.responsible}
          update={updateAccountHolder}
        />
      )}

      <Snackbar
        sx={{ top: "58px" }}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        open={!!remoteErrors}
        autoHideDuration={6000}
        onClose={() => setRemoteErrors(undefined)}
      >
        <Alert
          elevation={6}
          variant="filled"
          severity="error"
          onClose={() => setRemoteErrors(undefined)}
        >
          {remoteErrors}
        </Alert>
      </Snackbar>
    </>
  );
};
